import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, CircularProgress, FormControlLabel, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Button } from "abs-ui-components";
import axiosHttp from "../../services/interceptor";
import { emitToasterMsg } from "../../services/toasterUtility";
import AbsModal from "../../common/absModal/AbsModal";
import "./FuelRegistryTemplateWizard.scss";

const baseURL = process.env.REACT_APP_BASE_URL;
const templateApiUrl = `${baseURL}api/Templates/BuiltCustomFuelRegistryTemplate`;
const updateTemplateApiUrl = `${baseURL}api/Templates/UpdateFuelRegistryColumns`;
const validateTemplateApiUrl = `${baseURL}api/Templates/ValidateFuelRegistryTemplate`;
const templateColumnsApiUrl = `${baseURL}api/Templates/FuelRegistryTemplateColumns`;

const readField = (object, ...keys) => {
	for (const key of keys) {
		if (object && Object.prototype.hasOwnProperty.call(object, key)) {
			return object[key];
		}
	}
	return undefined;
};

const normalizeColumns = (columns = []) =>
	(Array.isArray(columns) ? columns : []).map((column) => {
		if (typeof column === "string") {
			return { key: column, label: column };
		}
		const key = readField(column, "key", "Key", "columnKey", "ColumnKey", "columnName", "ColumnName", "name", "Name");
		const label = readField(column, "label", "Label", "header", "Header", "displayName", "DisplayName") || key;
		return { key, label };
	}).filter((column) => column.key && column.label);

const getColumnNames = (columns = []) =>
	(Array.isArray(columns) ? columns : []).map((column) =>
		typeof column === "string"
			? column
			: readField(column, "label", "Label", "header", "Header", "name", "Name")
	).filter(Boolean);

const splitValidationColumns = (invalidColumns, missingPrimaryColumns = []) => {
	const invalidNames = [];
	const missingPrimaryNames = getColumnNames(missingPrimaryColumns);
	getColumnNames(invalidColumns).forEach((name) => {
		const missingMatch = name.match(/^(.*?)\s*\(required Primary column missing\)$/i);
		if (missingMatch) {
			missingPrimaryNames.push(missingMatch[1]);
		} else {
			invalidNames.push(name);
		}
	});
	return {
		invalidNames: [...new Set(invalidNames)],
		missingPrimaryNames: [...new Set(missingPrimaryNames)],
	};
};

const getErrorMessage = (error, fallback) =>
	error?.response?.data?.errorMessage ||
	error?.response?.data?.ErrorMessage ||
	error?.response?.data?.message ||
	error?.response?.data?.Message ||
	error?.message ||
	fallback;

const downloadWorkbook = (response, fallbackName) => {
	const contentDisposition = response?.headers?.["content-disposition"] || "";
	const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
	const fileName = fileNameMatch ? decodeURIComponent(fileNameMatch[1]) : fallbackName;
	const blob = new Blob([response.data], {
		type: response?.headers?.["content-type"] ||
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
};

const FuelRegistryTemplateWizard = ({ isOpen, onClose }) => {
	const [mode, setMode] = useState("new");
	const [primaryColumns, setPrimaryColumns] = useState([]);
	const [emissionFactorColumns, setEmissionFactorColumns] = useState([]);
	const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);
	const [uploadedFile, setUploadedFile] = useState(null);
	const [existingColumnKeys, setExistingColumnKeys] = useState([]);
	const [uploadInspectionSucceeded, setUploadInspectionSucceeded] = useState(false);
	const [invalidColumns, setInvalidColumns] = useState([]);
	const [missingPrimaryColumns, setMissingPrimaryColumns] = useState([]);
	const [loadingColumns, setLoadingColumns] = useState(false);
	const [inspectingFile, setInspectingFile] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const primaryKeySet = useMemo(
		() => new Set(primaryColumns.map((column) => column.key)),
		[primaryColumns]
	);
	const existingKeySet = useMemo(
		() => new Set(existingColumnKeys),
		[existingColumnKeys]
	);
	const primaryColumnKeys = useMemo(
		() => primaryColumns.map((column) => column.key),
		[primaryColumns]
	);

	useEffect(() => {
		if (!isOpen) return undefined;

		let isCurrent = true;
		setLoadingColumns(true);
		setErrorMessage("");
		axiosHttp.get(templateColumnsApiUrl)
			.then(({ data }) => {
				const result = data?.data || data?.Data || data || {};
				const primary = normalizeColumns(readField(result, "primaryColumns", "PrimaryColumns"));
				const emissionFactors = normalizeColumns(readField(result, "emissionFactorColumns", "EmissionFactorColumns"));
				if (!primary.length) {
					throw new Error("Template column metadata did not include Primary columns.");
				}
				if (isCurrent) {
					setPrimaryColumns(primary);
					setEmissionFactorColumns(emissionFactors);
					setSelectedColumnKeys(primary.map((column) => column.key));
				}
			})
			.catch((error) => {
				if (isCurrent) {
					setErrorMessage(getErrorMessage(error, "Unable to load template columns."));
				}
			})
			.finally(() => {
				if (isCurrent) setLoadingColumns(false);
			});

		return () => {
			isCurrent = false;
		};
	}, [isOpen]);

	const resetWizard = () => {
		setUploadedFile(null);
		setExistingColumnKeys([]);
		setUploadInspectionSucceeded(false);
		setInvalidColumns([]);
		setMissingPrimaryColumns([]);
		setErrorMessage("");
		setSelectedColumnKeys(primaryColumns.map((column) => column.key));
	};

	const selectFile = async (file) => {
		if (!file) return;
		if (!file.name.toLowerCase().endsWith(".xlsx")) {
			setUploadedFile(null);
			setUploadInspectionSucceeded(false);
			setInvalidColumns([]);
			setMissingPrimaryColumns([]);
			setErrorMessage("Only .xlsx files can be used to update a template.");
			return;
		}
		setUploadedFile(file);
		setExistingColumnKeys([]);
		setUploadInspectionSucceeded(false);
		setInvalidColumns([]);
		setMissingPrimaryColumns([]);
		setErrorMessage("");
		setInspectingFile(true);

		const formData = new FormData();
		formData.append("file", file);
		try {
			const { data } = await axiosHttp.post(validateTemplateApiUrl, formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			const result = data?.data || data?.Data || data || {};
			const { invalidNames } = splitValidationColumns(
				readField(result, "invalidColumns", "InvalidColumns"),
				readField(result, "missingPrimaryColumns", "MissingPrimaryColumns")
			);
			const recognized = readField(result, "recognizedColumnKeys", "RecognizedColumnKeys", "recognizedColumns", "RecognizedColumns") || [];
			const recognizedKeys = recognized.map((column) =>
				typeof column === "string"
					? column
					: readField(column, "key", "Key", "columnKey", "ColumnKey", "fieldName", "FieldName")
			).filter(Boolean);

			const missingPrimaryNames = [
				...splitValidationColumns(
					readField(result, "invalidColumns", "InvalidColumns"),
					readField(result, "missingPrimaryColumns", "MissingPrimaryColumns")
				).missingPrimaryNames,
				...primaryColumnKeys.filter(
				(columnKey) => !recognizedKeys.includes(columnKey)
				),
			];
			setInvalidColumns(invalidNames);
			setMissingPrimaryColumns([...new Set(missingPrimaryNames)]);
			if (invalidNames.length || missingPrimaryNames.length) {
				setErrorMessage("");
				return;
			}
			if (!recognizedKeys.length) {
				setErrorMessage("The uploaded file did not contain any recognized template columns.");
				return;
			}

			setExistingColumnKeys(recognizedKeys);
			setUploadInspectionSucceeded(true);
			setSelectedColumnKeys((current) => Array.from(new Set([
				...primaryColumnKeys,
				...recognizedKeys,
				...current.filter((key) => recognizedKeys.includes(key)),
			])));
		} catch (error) {
			const result = error?.response?.data || {};
			const { invalidNames, missingPrimaryNames } = splitValidationColumns(
				readField(result, "invalidColumns", "InvalidColumns"),
				readField(result, "missingPrimaryColumns", "MissingPrimaryColumns")
			);
			setInvalidColumns(invalidNames);
			setMissingPrimaryColumns(missingPrimaryNames);
			setUploadInspectionSucceeded(false);
			setErrorMessage(invalidNames.length || missingPrimaryNames.length
				? ""
				: getErrorMessage(error, "Unable to inspect the uploaded workbook."));
		} finally {
			setInspectingFile(false);
		}
	};

	const toggleColumn = (key) => {
		if (primaryKeySet.has(key) || existingKeySet.has(key)) return;
		setSelectedColumnKeys((current) => current.includes(key)
			? current.filter((selectedKey) => selectedKey !== key)
			: [...current, key]);
	};

	const handleDownload = async () => {
		setDownloading(true);
		setErrorMessage("");
		const canonicalColumnKeys = [...primaryColumns, ...emissionFactorColumns]
			.filter((column) => selectedColumnKeys.includes(column.key))
			.map((column) => column.key);

		try {
			let response;
			if (mode === "new") {
				response = await axiosHttp.post(templateApiUrl, canonicalColumnKeys, {
					responseType: "blob",
				});
			} else {
				const formData = new FormData();
				canonicalColumnKeys.forEach((columnHeader) => {
					formData.append("columnHeaders", columnHeader);
				});
				formData.append("file", uploadedFile);
				response = await axiosHttp.post(updateTemplateApiUrl, formData, {
					headers: { "Content-Type": "multipart/form-data" },
					responseType: "blob",
				});
			}
			downloadWorkbook(response, "FuelRegistryTemplate.xlsx");
		} catch (error) {
			let message = "Unable to download the fuel registry template.";
			if (error?.response?.data instanceof Blob) {
				message = (await error.response.data.text()) || message;
			} else {
				message = getErrorMessage(error, message);
			}
			if (mode === "existing") setErrorMessage(message);
			emitToasterMsg("error", "Fuel Registry Template", message);
		} finally {
			setDownloading(false);
		}
	};

	const handleClose = () => {
		resetWizard();
		onClose();
	};

	const downloadDisabled = loadingColumns || inspectingFile || downloading ||
		!primaryColumns.length ||
		invalidColumns.length > 0 || missingPrimaryColumns.length > 0 ||
		(mode === "existing" && (!uploadedFile || !uploadInspectionSucceeded));

	return (
		<AbsModal
			isOpen={isOpen}
			title="Download Fuel Registry Template"
			close={handleClose}
		>
			<div className="fuel-registry-template-wizard">
				<ToggleButtonGroup
					className="template-mode-toggle"
					value={mode}
					exclusive
					aria-label="Template starting point"
					disabled={inspectingFile || downloading}
					onChange={(_, nextMode) => {
						if (!nextMode || nextMode === mode) return;
						resetWizard();
						setMode(nextMode);
					}}
				>
					<ToggleButton value="new">Build New</ToggleButton>
					<ToggleButton value="existing">Update Existing</ToggleButton>
				</ToggleButtonGroup>

				{mode === "existing" && (
					<>
						<label
							className="template-upload-area"
							onDragOver={(event) => event.preventDefault()}
							onDrop={(event) => {
								event.preventDefault();
								selectFile(event.dataTransfer.files?.[0]);
							}}
						>
							<input
								type="file"
								accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
								onChange={(event) => selectFile(event.target.files?.[0])}
							/>
							<span>{uploadedFile ? uploadedFile.name : "Drop an existing .xlsx template here or browse"}</span>
						</label>
						{uploadedFile && (
							<Button className="clear-template-file" variant="secondary" onClick={resetWizard}>
								Remove uploaded file
							</Button>
						)}
					</>
				)}

				<div className="template-column-groups">
					<section aria-labelledby="primary-template-columns">
						<h3 id="primary-template-columns">Required columns</h3>
						{primaryColumns.map((column) => (
							<FormControlLabel
								key={column.key}
								control={<Checkbox checked disabled />}
								label={column.label}
							/>
						))}
					</section>
					<section aria-labelledby="emission-factor-template-columns">
						<h3 id="emission-factor-template-columns">Optional columns</h3>
						{emissionFactorColumns.map((column) => {
							const checked = selectedColumnKeys.includes(column.key);
							const locked = existingKeySet.has(column.key);
							return (
								<FormControlLabel
									key={column.key}
									control={<Checkbox checked={checked} disabled={locked || inspectingFile} onChange={() => toggleColumn(column.key)} />}
									label={column.label}
								/>
							);
						})}
					</section>
				</div>

				{inspectingFile && <div className="template-status"><CircularProgress size={18} /> Checking uploaded columns...</div>}
				{mode === "existing" && uploadedFile && existingColumnKeys.length > 0 && !inspectingFile && !errorMessage && (
					<p className="template-status">Columns in the uploaded file are selected and locked.</p>
				)}
				{invalidColumns.length > 0 && (
					<div className="template-error" role="alert">
						Unrecognized or invalid columns: {invalidColumns.join(", ")}
					</div>
				)}
				{missingPrimaryColumns.length > 0 && (
					<div className="template-error" role="alert">
						Missing required columns: {missingPrimaryColumns.join(", ")}
					</div>
				)}
				{errorMessage && <div className="template-error" role="alert">{errorMessage}</div>}
				<div className="template-wizard-actions">
					<Button variant="secondary" onClick={handleClose}>Cancel</Button>
					<Button onClick={handleDownload} disabled={downloadDisabled}>
						{downloading ? <CircularProgress size={18} /> : "Download"}
					</Button>
				</div>
			</div>
		</AbsModal>
	);
};

export default FuelRegistryTemplateWizard;